'use client';
import {
    faBriefcase,
    faBuilding,
    faCoins,
    faGraduationCap,
    faHospital,
    faIndustry,
    faSeedling,
    faStore,
    faTruck,
} from '@fortawesome/free-solid-svg-icons';
import { Button } from '@material-tailwind/react';
import ChangeText from './ChangeText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fragment } from 'react';
import Frappe from './Frappe';
import Image from 'next/image';
import Link from 'next/link';
import LogosMarquee from './LogosMarquee';
import logosData from '../textContent/logos/logosDataSlim.json';
import { motion } from 'framer-motion';

export default function Home() {
    const textContent = [
        'streamline operations',
        'automate production',
        'increase profits',
        'reduce costs',
        'boost productivity',
    ];

    const solutions = [
        {
            name: 'Manufacturing',
            icon: faIndustry,
            link: '/solutions/manufacturing',
        },
        {
            name: 'Distribution',
            icon: faTruck,
            link: '/solutions/distribution',
        },
        { name: 'Accounts', icon: faCoins, link: '/solutions/accounting' },
        { name: 'Healthcare', icon: faHospital, link: '/solutions/healthcare' },
        { name: 'Retail', icon: faStore, link: '/solutions/retail' },
        { name: 'Services', icon: faBriefcase, link: '/solutions/services' },
        {
            name: 'Education',
            icon: faGraduationCap,
            link: '/solutions/education',
        },
        {
            name: 'Real Estate',
            icon: faBuilding,
            link: '/solutions/real-estate',
        },
        {
            name: 'Agriculture',
            icon: faSeedling,
            link: '/solutions/agriculture',
        },
    ];

    const fadeInUp = {
        hidden: { opacity: 0, y: 60 },
        visible: { opacity: 1, y: 0 },
    };

    const fadeIn = {
        hidden: { opacity: 0 },
        visible: { opacity: 1 },
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
            },
        },
    };

    return (
        <Fragment>
            <div id="page-1" className="h-full bg-opacity-80">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8 }}
                    className="md:hidden"
                >
                    <video
                        src="/images/home-video.webm"
                        autoPlay={true}
                        muted={true}
                        loop
                        playsInline
                        className="w-full h-[260px] object-cover px-6"
                        id="topimg"
                    />
                </motion.div>

                <div id="content">
                    <div className="w-full max-h-screen grid md:grid-cols-2">
                        <div className="mx-4 md:mx-24 md:py-12">
                            <div className="hero-text-animate">
                                <ChangeText
                                    staticText="We have the ERP solutions to"
                                    animTextContent={textContent}
                                    interval={2000}
                                    staticTextClass="text-3xl h-auto md:h-auto md:text-4xl font-bold mt-[5vw] md:mt-[3vw] w-full text-[#393939]"
                                    animTextClass="w-full typed"
                                />
                            </div>
                            <motion.div
                                initial="hidden"
                                animate="visible"
                                variants={staggerContainer}
                                className="bg-transparent py-4 rounded-md mt-8"
                                id="btn"
                            >
                                <div className="flex flex-wrap gap-4">
                                    {solutions.map((solution) => (
                                        <motion.div
                                            key={solution.link}
                                            variants={fadeInUp}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <Link
                                                rel="canonical"
                                                href={solution.link}
                                            >
                                                <button className="py-2 px-4 bg-transparent rounded-md hover:shadow-2xl shadow-md font-semibold flex items-center transition gap-2">
                                                    <FontAwesomeIcon
                                                        icon={solution.icon}
                                                        className="text-blue-600"
                                                    />
                                                    {solution.name}
                                                </button>
                                            </Link>
                                        </motion.div>
                                    ))}
                                </div>
                            </motion.div>

                            <motion.div
                                initial="hidden"
                                animate="visible"
                                variants={fadeInUp}
                                transition={{ duration: 0.6, delay: 0.4 }}
                                className="mt-8"
                            >
                                <Link
                                    rel="canonical"
                                    href="/contact-sales"
                                    scroll={true}
                                >
                                    <Button variant="gradient" color="blue">
                                        Schedule a Free Demo
                                    </Button>
                                </Link>
                            </motion.div>

                            <motion.div
                                initial="hidden"
                                animate="visible"
                                variants={fadeInUp}
                                transition={{ duration: 0.6, delay: 0.6 }}
                                className="mt-[8vw] md:mt-[3vw] mx-[5vw] md:mx-[0vw]"
                                id="qualifications"
                            >
                                <Frappe />

                                <p>
                                    <i className="ri-star-fill text-yellow-300 text-2xl"></i>
                                    <i className="ri-star-fill text-yellow-300 text-2xl"></i>
                                    <i className="ri-star-fill text-yellow-300 text-2xl"></i>
                                    <i className="ri-star-fill text-yellow-300 text-2xl"></i>
                                    <span className="text-sm text-gray-600 ml-1">
                                        Based on Customer Reviews
                                    </span>
                                </p>
                            </motion.div>
                        </div>
                        <motion.div
                            initial={{ opacity: 0, x: 100 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            id="right"
                            className="hidden md:flex items-center justify-start w-full h-full py-8 pr-4"
                        >
                            <video
                                alt="video"
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    minHeight: '500px',
                                }}
                                src={'/images/home-video.webm'}
                                autoPlay
                                loop
                                muted
                                playsInline
                            />
                        </motion.div>
                    </div>

                    <motion.div
                        initial="hidden"
                        animate="visible"
                        variants={fadeIn}
                        transition={{ duration: 0.8 }}
                        className="flex items-center justify-center flex-col py-4 md:py-6 opacity-60"
                    >
                        <LogosMarquee data={logosData} />
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={fadeInUp}
                        transition={{ duration: 0.8 }}
                        className="mx-10 my-14 lg:my-1 lg:mx-2"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2">
                            <motion.div
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6 }}
                            >
                                <Image
                                    src="/images/blog1.webp"
                                    height={500}
                                    width={500}
                                    alt="erp"
                                    priority
                                />
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                            >
                                <h2 className="text-2xl font-semibold">
                                    Transform Your Business with the best ERP
                                    Software
                                </h2>

                                <div className="mt-4">
                                    <p>
                                        Staying ahead in the fast-paced business
                                        environment of today necessitates not
                                        just creativity but also accuracy,
                                        efficacy, and smooth integration in all
                                        facets of your business operations. We
                                        are more than simply a technology
                                        business.
                                    </p>
                                    <p className="mt-6">
                                        By offering the best ERP Software in
                                        Kerala, Wahni IT Solutions helps you
                                        navigate the complex world of ERP unlock
                                        your full potential and redefine
                                        success.
                                    </p>
                                </div>

                                <div className="mt-4">
                                    <Link rel="canonical" href="/contact-sales">
                                        <Button variant="gradient" color="blue">
                                            Contact Sales
                                        </Button>
                                    </Link>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </div>
            <style>{`
                @keyframes heroFadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(60px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .hero-text-animate {
                    animation: heroFadeInUp 0.6s ease forwards;
                }
            `}</style>
        </Fragment>
    );
}
